import TEXT from "../../../utils/globalText";
import ICONS from "../../../assets/Icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.css";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import DebitCreditAmount from "../../../components/Settings/Statistics/GraphsComponents/DebitCreditAmount";
import IdleFund from "../../../components/Settings/Statistics/GraphsComponents/IdleFund";
import OdInterestSaved from "../../../components/Settings/Statistics/GraphsComponents/OdInterestSaved";
import TransactionCounts from "../../../components/Settings/Statistics/GraphsComponents/TransactionCounts";
import ProductivityTracker from "../../../components/Settings/Statistics/GraphsComponents/ProductivityTracker";
import CashCoveragePeriod from "../../../components/Settings/Statistics/GraphsComponents/CashCoveragePeriod";
import AccountsPerDay from "../../../components/Settings/Statistics/GraphsComponents/AccountsPerDay";
import TransactionPerDay from "../../../components/Settings/Statistics/GraphsComponents/TransactionPerDay";
import SVGComponent from "../../../components/_utils/SVGComponent";
import {
  addWidget,
  removeWidget,
  fetchWidgets,
} from "../../../redux/features/Settings/Statistics";
import PrimaryWithIconButton from "../../../components/_utils/CommonButtons/PrimaryWithIconButton";
import {
  WIDGET_TITLE_TO_ID,
  WIDGET_IDS,
} from "../../HomeDashboard/Widgets/WidgetRegistry";
import {
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../utils/configurationProvider";
import IncomeOnIdleFunds from "../../../components/Settings/Statistics/GraphsComponents/IncomeOnIdleFunds";

/**
 * Statistics container component for displaying financial analytics graphs.
 * Renders a scrollable list of statistic cards (Debit/Credit, Idle Funds, Productivity,
 * Transaction Count, OD Interest Saved, Cash Coverage) with a sidebar navigation.
 * Integrates with Redux for widget persistence on the home dashboard.
 * @returns {JSX.Element} The Statistics page component
 */
const Statistics = () => {
  const services = useSelector((state) => state.refreshToken.services);
  const { widgetsData } = useSelector((state) => state.settingStatistics);
  const [graphIsOpenInResp, setGraphIsOpenInResp] = useState("");
  const [countOfClicked, setCountOfClicked] = useState(
    GlobalConfiguration?.lengthCheckOne,
  );
  const [activeIndex, setActiveIndex] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const debitCreditRef = useRef(null);
  const idleFundsRef = useRef(null);
  const incomeOnIdleFundsRef = useRef(null);
  const odInterestRef = useRef(null);
  const txnCountRef = useRef(null);
  const prodTrackerRef = useRef(null);
  const cashCoverageRef = useRef(null);
  const accountsPerDayRef = useRef(null);
  const transactionPerDayRef = useRef(null);

  /**
   * Memoized configuration of available statistics graphs and their corresponding refs.
   * The order of options must match the order of refs for proper scroll navigation.
   */
  const { graphOptions, graphRefs } = useMemo(() => {
    let options = [
      TEXT?.DEBIT_AND_CREDIT, //"Debit and credit amount",
      TEXT?.IDLE_FUNDS, //"Idle funds",
      TEXT?.INCOME_ON_IDLE_FUNDS,
      TEXT?.PRODUCTIVITY_TRACKER, //"Productivity tracker",
      TEXT?.TRANSACTIONS_COUNT, //"Transaction count",
      TEXT?.OD_INTEREST_SAVED, //"OD interest saved",
      TEXT?.CASH_COVERAGE_PERIOD, //"Cash coverage period",
      TEXT?.ACCOUNTS_PER_DAY, //"Accounts per day",
      TEXT?.TRANSACTIONS_PER_DAY, //"Transactions per day",
    ];

    let refs = [
      debitCreditRef,
      idleFundsRef,
      incomeOnIdleFundsRef,
      prodTrackerRef,
      txnCountRef,
      odInterestRef,
      cashCoverageRef,
      accountsPerDayRef,
      transactionPerDayRef,
    ];

    return { graphOptions: options, graphRefs: refs };
  }, [services]);

  /**
   * Handles scroll navigation between statistic cards using up/down arrow buttons.
   * Updates the active index and scrolls the container to the target card.
   */
  const handleScrollChange = (direction, containerRef) => {
    if (!containerRef?.current) return;

    const container = containerRef.current;
    const totalCards = graphOptions.length;
    let newIndex = countOfClicked;

    // "up" moves to next card (higher index), "down" moves to previous (lower index)
    if (direction === "up" && countOfClicked < totalCards) newIndex++;
    else if (
      direction === "down" &&
      countOfClicked > GlobalConfiguration?.lengthCheckOne
    )
      newIndex--;
    else return;

    const targetRef = graphRefs[newIndex - GlobalConfiguration?.lengthCheckOne];
    if (targetRef?.current) {
      const targetTop = targetRef.current.offsetTop - container.offsetTop;
      container.scrollTo({ top: targetTop });
    }

    setCountOfClicked(newIndex);
    setGraphIsOpenInResp(
      graphOptions[newIndex - GlobalConfiguration?.lengthCheckOne],
    );
  };

  /**
   * Creates an array of graph objects with name and scroll handler for sidebar navigation.
   * Each entry corresponds to a statistic card and handles scrolling to that card when clicked.
   */
  const graphsObject = graphOptions.map((name, idx) => ({
    name,
    handleFunction: () => {
      const container = containerRef.current;
      const targetCard = graphRefs[idx].current;

      if (container && targetCard) {
        // Compute top position of the card relative to container
        const targetTop =
          targetCard.offsetTop -
          container.offsetTop -
          SettingsStatisticsConfiguration?.targetGraphCardTopOffset;

        container.scrollTo({
          top: targetTop,
        });
      }

      // update states
      setGraphIsOpenInResp(name);
      setCountOfClicked(idx + GlobalConfiguration?.lengthCheckOne);
    },
  }));

  /**
   * Memoized set of currently active widget IDs from Redux state.
   * Used to determine which statistics cards are pinned to the home dashboard.
   */
  const activeWidgetIds = useMemo(() => {
    if (!widgetsData?.widgets) return new Set();
    return new Set(widgetsData.widgets.map((widget) => widget.widget_id));
  }, [widgetsData]);

  // Toggle or add/remove handler for widgets
  const handleToggleHomeWidget = (widgetId, shouldAdd) => {
    if (shouldAdd) {
      dispatch(
        addWidget({
          widget_id: widgetId,
        }),
      );
    } else {
      dispatch(removeWidget({ widget_id: widgetId }));
    }
  };

  /**
   * Fetches the user's saved home dashboard widgets on component mount.
   * Runs once since dispatch is stable.
   */
  useEffect(() => {
    dispatch(fetchWidgets());
  }, [dispatch]);

  /**
   * Scroll listener to sync sidebar navigation with the currently visible statistic card.
   * Calculates which card is closest to the top of the scroll container and updates
   * the active index and mobile header accordingly.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;

      // Find the card closest to the top of the viewport
      let closestIndex = GlobalConfiguration?.lengthCheckZero;
      let minDistance = Infinity;

      graphRefs.forEach((ref, idx) => {
        const cardTop = ref.current.offsetTop - container.offsetTop;
        const distance = Math.abs(scrollTop - cardTop);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = idx;
        }
      });

      // Update only when index actually changes
      setActiveIndex((prev) => {
        if (prev !== closestIndex) {
          setCountOfClicked(closestIndex + GlobalConfiguration?.lengthCheckOne);
          setGraphIsOpenInResp(graphOptions[closestIndex]);
        }
        return closestIndex;
      });
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [graphOptions, graphRefs]);

  return (
    <div className="min-h-full-height h-height-full w-full-width relative text-text-primary flex flex-col ">
      <div className="h-height-full w-width-full flex flex-col sticky top-top-base z-z-index-tooltip bg-primary-nav">
        <div className="h-navbar-breadcrumb-height Qsmd:block hidden"></div>
        <>
          <div
            className={`Qsmd:hidden w-full h-mobile-create-header ${
              graphIsOpenInResp
                ? "shadow-box-shadow-bottom z-z-index-fixed-ui"
                : ""
            }`}
          >
            <div className="max-w-tab-width-normal mx-auto w-full flex Qsmd:flex-row flex-col">
              <div className="Qsmd:hidden Qsmd:max-w-statistic-header-left-section-width Qsmd:w-width-full w-full-width p-padding-md Qsmd:bg-primary-nav-act bg-primary-nav flex items-center gap-gap-sm h-mobile-create-header">
                <img
                  className="w-icon-sm h-icon-sm Qsmd:flex hidden"
                  src={ICONS?.STATISTICS}
                  alt="STATISTICS"
                />
                <div
                  className="w-icon-sm h-icon_w-icon-sm Qsmd:hidden flex cursor-pointer"
                  onClick={() => navigate(-1)}
                >
                  <SVGComponent
                    url={ICONS?.ARROW_LEFT_WHITE}
                    color={"var(--text-primary)"}
                  />
                </div>
                <p className="text-font-md text-text-primary font-normal">
                  {TEXT?.STATISTICS}
                </p>
              </div>
            </div>
          </div>

          {graphIsOpenInResp && (
            <div
              className="Qsmd:max-w-statistic-header-left-section-width Qsmd:w-width-full w-full-width p-padding-md bg-primary-nav-act Qsmd:hidden flex items-center gap-gap-sm h-mobile-create-header justify-between z-z-index-tooltip relative"
              onClick={() => setGraphIsOpenInResp("")}
            >
              <p className="text-text-primary text-font-sm">
                {graphIsOpenInResp}
              </p>
              <div className="w-icon-sm h-icon-sm cursor-pointer">
                <SVGComponent
                  url={ICONS?.ARROW_RIGHT_WHITE}
                  color={"var(--text-primary)"}
                  height={"var(--icon-xs)"}
                  width={"var(--icon-xs)"}
                  alt="Arrow"
                />
              </div>
            </div>
          )}
        </>
      </div>
      <div className="flex flex-1 justify-center w-width-full overflow-y-hidden">
        <div className="bg-primary-card flex-1"></div>
        <div className="max-w-tab-width-normal w-width-full flex">
          <div
            className={`h-screen Qmd:max-w-statistic-header-left-section-width w-width-full bg-primary-card pl-padding-md overflow-y-hidden  Qmd:h-screen fixed ${graphIsOpenInResp !== "" ? "Qsmd:z-z-index-toast z-z-index-base" : "z-z-index-toast"}`}
          >
            {graphsObject?.map((value, index) => {
              return (
                <div>
                  <div
                    className={`flex flex-col ${index ? "mt-margin-top-for-statistics-tab-dot" : ""}`}
                  >
                    <div className="h-height-of-statistics-tab-line w-width-of-statistics-tab-line bg-primary-act"></div>
                    <div className="flex gap-gap-xs items-center ml-margin-left-for-statistics-tab-dot mt-margin-top-for-statistics-tab-dot">
                      <div className="border-border border-primary-nav-act rounded-border-radius-sm p-[var(--border)] h-[max-content] items-center">
                        <div
                          className={`bg-text-secondary rounded-border-radius-sm ${activeIndex === index ? "py-padding-xs px-padding-button-m" : "p-padding-xs"} transition-[width] duration-animation-fast-s ease-in`}
                        ></div>
                      </div>
                      <div className="flex items-center gap-gap-xs">
                        {activeWidgetIds.has(
                          WIDGET_TITLE_TO_ID[value?.name],
                        ) ? (
                          <PrimaryWithIconButton
                            btnText={value?.name}
                            enabledBgColor={
                              activeIndex === index
                                ? "var(--primary-nav-act)"
                                : "var(--primary-card)"
                            }
                            enabledTextColor={"var(--text-primary)"}
                            handleFunction={value.handleFunction}
                            hoverOption="white"
                            icon={
                              activeWidgetIds.has(
                                WIDGET_TITLE_TO_ID[value?.name],
                              )
                                ? ICONS?.HOME_UNSELECTED
                                : ""
                            }
                            textSemibold={true}
                            iconToBeAlign="right"
                          />
                        ) : (
                          <PrimaryButton
                            btnText={value?.name}
                            enabledBgColor={
                              activeIndex === index
                                ? "var(--primary-nav-act)"
                                : "var(--primary-card)"
                            }
                            enabledTextColor={"var(--text-primary)"}
                            handleFunction={value.handleFunction}
                            hoverOption="white"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="w-width-full max-w-statistic-header-left-section-width h-height-full Qmd:block hidden"></div>

          <div
            className={`flex-1 h-height-full relative w-width-full bg-primary-bg flex  Qmd:h-[calc(var(--full-height)-(var(--mobile-navbar-height)+var(--navbar-breadcrumb-height)+var(--stepper-line-height)))]`}
          >
            <div className="Qlg:flex hidden bottom-bottom-label-tab absolute z-z-index-content right-right-base flex-col gap-gap-sm">
              <div
                className={`w-icon-md h-icon-md ${countOfClicked <= GlobalConfiguration?.lengthCheckOne ? "opacity-light-opacity cursor-not-allowed" : "cursor-pointer"}`}
              >
                <img
                  className="w-icon-md h-icon-md"
                  src={ICONS?.BLUE_ARROW_UP_WHITE_CIRCLE}
                  alt="ArrowUpWhiteCircle"
                  onClick={() => handleScrollChange("down", containerRef)}
                />
              </div>
              <div
                className={`w-icon-md h-icon-md ${countOfClicked >= graphOptions.length ? "opacity-light-opacity cursor-not-allowed" : "cursor-pointer"}`}
              >
                <img
                  className="w-icon-md h-icon-md"
                  src={ICONS?.BLUE_ARROW_DOWN_WHITE_CIRCLE}
                  alt="ArrowDownWhiteCircle"
                  onClick={() => handleScrollChange("up", containerRef)}
                />
              </div>
            </div>
            <div
              ref={containerRef}
              id="main-scrollable-container"
              className=" h-screen flex flex-1 flex-col gap-gap-md w-width-full Qsmd:pt-padding-md px-padding-md Qmd:px-padding-xl absolute scroll-p-top_md"
            >
              <div
                className="scroll-card pt-padding-md"
                ref={debitCreditRef}
              >
                <DebitCreditAmount
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.DEBIT_CREDIT_AMOUNT,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(
                      WIDGET_IDS.DEBIT_CREDIT_AMOUNT,
                      status,
                    )
                  }
                />
              </div>
              <div
                className="scroll-card"
                ref={idleFundsRef}
              >
                <IdleFund />
              </div>

              <div
                className="scroll-card"
                ref={incomeOnIdleFundsRef}
              >
                <IncomeOnIdleFunds
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.INCOME_ON_IDLE_FUNDS,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(
                      WIDGET_IDS.INCOME_ON_IDLE_FUNDS,
                      status,
                    )
                  }
                  autoFetch={false}
                />
              </div>
              <div
                className="scroll-card"
                ref={prodTrackerRef}
              >
                <ProductivityTracker
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.PRODUCTIVITY_TRACKER,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(
                      WIDGET_IDS.PRODUCTIVITY_TRACKER,
                      status,
                    )
                  }
                />
              </div>
              <div
                className="scroll-card"
                ref={txnCountRef}
              >
                <TransactionCounts
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.TRANSACTION_COUNT,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(WIDGET_IDS.TRANSACTION_COUNT, status)
                  }
                  autoFetch={false}
                />
              </div>
              <div
                className="scroll-card"
                ref={odInterestRef}
              >
                <OdInterestSaved
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.OD_INTEREST_SAVED,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(WIDGET_IDS.OD_INTEREST_SAVED, status)
                  }
                  autoFetch={false}
                />
              </div>

              <div
                className="scroll-card"
                ref={cashCoverageRef}
              >
                <CashCoveragePeriod
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.CASH_COVERAGE_PERIOD,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(
                      WIDGET_IDS.CASH_COVERAGE_PERIOD,
                      status,
                    )
                  }
                  autoFetch={false}
                />
              </div>

              <div
                className="scroll-card"
                ref={accountsPerDayRef}
              >
                <AccountsPerDay
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.ACCOUNTS_PER_DAY,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(WIDGET_IDS.ACCOUNTS_PER_DAY, status)
                  }
                  autoFetch={false}
                />
              </div>
              <div
                className="scroll-card"
                ref={transactionPerDayRef}
              >
                <TransactionPerDay
                  isAddedToHome={activeWidgetIds.has(
                    WIDGET_IDS.TRANSACTION_PER_DAY,
                  )}
                  toggleAddedToHome={(status) =>
                    handleToggleHomeWidget(
                      WIDGET_IDS.TRANSACTION_PER_DAY,
                      status,
                    )
                  }
                  autoFetch={false}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-primary-bg flex-1"></div>
      </div>
    </div>
  );
};

export default Statistics;
